import type { CreditAllowanceNudgeThreshold } from '../trials-and-upgrades/credit-limit-reached-upgrade/types';

export type AgentCreditAllowanceBannerSurface = {
  kind: 'upgrade';
  threshold: CreditAllowanceNudgeThreshold;
  dismissible: boolean;
};

export function resolveAgentCreditAllowanceBanner({
  isOptInCreditUsageEnabled,
  isMaxSeat,
  threshold,
  isConsumed,
  forceExhausted = false,
  isUsageNudgesEnabled = false,
}: {
  isOptInCreditUsageEnabled: boolean;
  isMaxSeat: boolean;
  threshold: CreditAllowanceNudgeThreshold | null;
  isConsumed: boolean;
  /** 402 just landed — paint 100 even if GET still shows remaining > 0. */
  forceExhausted?: boolean;
  /**
   * Usage-nudges ON: Standard 100 is dismissible in chat — honor the ledger.
   * Flag off: sticky hard-sell; ignore isConsumed (no close control).
   */
  isUsageNudgesEnabled?: boolean;
}): AgentCreditAllowanceBannerSurface | null {
  if (!isOptInCreditUsageEnabled) {
    return null;
  }

  const resolvedThreshold: CreditAllowanceNudgeThreshold | null = forceExhausted
    ? 100
    : threshold;
  if (resolvedThreshold == null) {
    return null;
  }

  // Max already has top-tier allowance — no in-chat / routines credit banner.
  if (isMaxSeat) {
    return null;
  }

  if (resolvedThreshold === 100) {
    if (isUsageNudgesEnabled && isConsumed) {
      return null;
    }
    return { kind: 'upgrade', threshold: 100, dismissible: isUsageNudgesEnabled };
  }

  // 50/75 rungs are retired — only 90 and 100 still nudge.
  if (resolvedThreshold !== 90 || isConsumed) {
    return null;
  }

  return { kind: 'upgrade', threshold: 90, dismissible: true };
}

type CreditAllowanceSendGateArgs = {
  isOptInCreditUsageEnabled: boolean;
  isGateEnabled: boolean;
  isMaxSeat: boolean;
  threshold: CreditAllowanceNudgeThreshold | null;
  forceExhausted?: boolean;
  /** Usage-nudges ON: Send stays open at 100. Flag off greys Send. */
  isUsageNudgesEnabled?: boolean;
};

/** Standard seat at remaining <= 0 (GET 100 or a 402 hold). Max never matches. */
function isStandardSeatCreditExhausted({
  isOptInCreditUsageEnabled,
  isGateEnabled,
  isMaxSeat,
  threshold,
  forceExhausted = false,
}: CreditAllowanceSendGateArgs): boolean {
  return (
    isOptInCreditUsageEnabled &&
    isGateEnabled &&
    !isMaxSeat &&
    (forceExhausted || threshold === 100)
  );
}

/**
 * Usage-nudges ON: never greys Send. Flag off: Standard seat at remaining <= 0
 * (GET 100 or a 402 hold) greys Send; Max stays sendable.
 */
export function shouldSkipCreditAllowanceSend({
  isUsageNudgesEnabled = false,
  ...exhaustion
}: CreditAllowanceSendGateArgs): boolean {
  if (isUsageNudgesEnabled) {
    return false;
  }
  return isStandardSeatCreditExhausted(exhaustion);
}

/**
 * Usage-nudges ON + Standard seat exhausted: Evidence only defaults on;
 * clearing it greys Work Send (Heidi 2 stays gated). Flag off / Max: never.
 */
export function shouldForceEvidenceOnlyOnCreditExhausted({
  isUsageNudgesEnabled = false,
  ...exhaustion
}: CreditAllowanceSendGateArgs): boolean {
  return isUsageNudgesEnabled && isStandardSeatCreditExhausted(exhaustion);
}

export type AgentCreditAllowanceBannerPlacement = 'composer' | 'transcript';

/** Work home: never. In-thread: nudges ON → 100 on composer; off → 90 composer / 100 transcript. */
export function placeAgentCreditAllowanceBanner({
  surface,
  inThread,
  isUsageNudgesEnabled = false,
}: {
  surface: AgentCreditAllowanceBannerSurface | null;
  inThread: boolean;
  isUsageNudgesEnabled?: boolean;
}): AgentCreditAllowanceBannerPlacement | null {
  if (surface == null || !inThread) {
    return null;
  }
  if (isUsageNudgesEnabled) {
    return surface.threshold === 100 ? 'composer' : null;
  }
  if (surface.threshold === 100) {
    return 'transcript';
  }
  return surface.threshold === 90 ? 'composer' : null;
}
